import React, { useEffect } from 'react'
import type {
  AppointmentInterviewResource,
  ClinicPatientInterviewResource,
  InterviewAnswerResource,
  InterviewQuestionResource,
  InterviewResource,
} from '@entities/api/Interview'
import useApiAction from '@hooks/useApiAction'
import useErrorModal from '@hooks/useErrorModal'
import jsonApi from '@utils/jsonApi'
import { FormProvider, useForm } from 'react-hook-form'
import Agreement from './interview/Agreement'
import DateTime from './interview/DateTime'
import Explanation from './interview/Explanation'
import HeightWeight from './interview/HeightWeight'
import History from './interview/History'
import InputNumber from './interview/InputNumber'
import InputText from './interview/InputText'
import Menstruation from './interview/Menstruation'
import Multiple from './interview/Multiple'
import Single from './interview/Single'
import YearMonth from './interview/YearMonth'
import YesNo from './interview/YesNo'

interface Props {
  appointment: AppointmentInterviewResource
  patient: ClinicPatientInterviewResource
  interview: InterviewResource
  questions: InterviewQuestionResource[]
  answers: InterviewAnswerResource[]
  baseUrl: string
}

const inArrayOrEqual = (target: string, test: string | Array<string>): boolean => {
  const splitTarget = target?.split('|')
  if (splitTarget && splitTarget.length > 1) {
    if (test instanceof Array) {
      return splitTarget.some((t) => test.indexOf(t) >= 0)
    }
    return splitTarget.indexOf(test) >= 0
  }

  if (test instanceof Array) {
    return test.indexOf(target) >= 0
  }
  return target === test
}

const InterviewForm: React.FC<Props> = ({
  appointment,
  patient,
  interview,
  questions,
  answers: defaultAnswers,
  baseUrl,
}) => {
  const answerHash = {}
  defaultAnswers.forEach((answer) => {
    answerHash[answer.interview_question_id] = answer
  })
  const questionAnswers = questions.map((question) => ({
    question,
    answer: answerHash[question.id],
  }))

  const showError = useErrorModal()
  const { run, disableAction } = useApiAction({
    systemMessage: '回答を記録できませんでした。',
    networkMessage: '回答を記録できませんでした。',
  })
  const formMethods = useForm({
    defaultValues: {
      interview: {
        position: interview.position,
      },
      answers: questionAnswers.map(({ question, answer }) => ({
        id: answer.id,
        value:
          question.question_type !== 'height_weight' &&
          question.question_type !== 'menstruation' &&
          question.question_type !== 'datetime' &&
          question.question_type !== 'history'
            ? answer.value
            : null,
        height_weight_value: question.question_type === 'height_weight' ? answer.value : null,
        menstruation_value: question.question_type === 'menstruation' ? answer.value : null,
        datetime_value: question.question_type === 'datetime' ? answer.value : null,
        history_value: question.question_type === 'history' ? answer.value : null,
      })),
    },
    shouldFocusError: false,
  })
  const { register, errors, watch, handleSubmit, setValue, getValues } = formMethods

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      showError({ title: '入力エラー', reason: errors })
    }
  }, [errors])

  const now = Math.round(((interview.position + 1) / interview.length) * 1000) / 10
  const questionIndexHash = {}
  questionAnswers.forEach(({ question }, index) => {
    questionIndexHash[question.id] = index
  })
  const answers = watch('answers')

  const handleSend = (data) => {
    run(() => jsonApi(`${baseUrl}.json`, 'put', data)).then((r) => {
      if (r.ok) window.location.reload()
    })
  }

  const handlePrev = () => {
    run(() =>
      jsonApi(`${baseUrl}/prev.json`, 'put', {
        interview: {
          position: interview.position,
        },
      })
    ).then((r) => {
      if (r.ok) window.location.reload()
    })
  }

  return (
    <FormProvider {...formMethods}>
      <div className="container-sm pb-5">
        <div className="row justify-content-center pt-4 pb-4">
          <div className="col-auto font-weight-bold">{interview.name}</div>
        </div>
        <div className="row">
          <div className="col">
            <div className="progress zm-progress rounded-pill">
              <div
                className="progress-bar rounded-pill"
                role="progressbar"
                style={{ width: `${now}%` }}
                aria-valuenow={now}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${now}%`}
              />
            </div>
          </div>
        </div>
        <div className="row justify-content-center pt-1 pb-2">
          <div className="col-auto">
            {interview.position + 1}
            問目
            <span className="mx-2">/</span>全{interview.length}問
          </div>
        </div>
        <form onSubmit={handleSubmit(handleSend)}>
          <input ref={register({ valueAsNumber: true })} type="hidden" name="interview.position" />
          {questions.map((question, index) => {
            const prefix = `answers.${index}`
            if (
              !question.parent_id ||
              !question.parent_condition ||
              inArrayOrEqual(
                answers[questionIndexHash[question.parent_id]].value,
                question.parent_condition
              )
            ) {
              return (
                <div key={question.id} className="row pt-4">
                  <div className="col">
                    <input
                      ref={register({ valueAsNumber: true })}
                      type="hidden"
                      name={`${prefix}.id`}
                    />
                    <div className="row pb-3">
                      <div className="col">
                        {question.required && (
                          <span className="badge badge-primary align-text-bottom font-weight-normal mr-2">
                            必須
                          </span>
                        )}
                        <span className="font-weight-bold">{question.body}</span>
                        {question.options?.explanation && (
                          <Explanation
                            id={`${prefix}_explanation`}
                            explanation={question.options.explanation}
                          />
                        )}
                      </div>
                    </div>
                    {question.question_type === 'yes_no' && (
                      <YesNo
                        prefix={prefix}
                        itemName={question.item_name}
                        required={question.required}
                        options={question.options}
                      />
                    )}
                    {question.question_type === 'number' && (
                      <InputNumber
                        prefix={prefix}
                        itemName={question.item_name}
                        required={question.required}
                        options={question.options}
                        validations={question.validations}
                      />
                    )}
                    {question.question_type === 'text' && (
                      <InputText
                        prefix={prefix}
                        itemName={question.item_name}
                        required={question.required}
                        options={question.options}
                        validations={question.validations}
                      />
                    )}
                    {question.question_type === 'single' && (
                      <Single
                        prefix={prefix}
                        itemName={question.item_name}
                        choices={question.choices}
                        required={question.required}
                      />
                    )}
                    {question.question_type === 'multiple' && (
                      <Multiple
                        prefix={prefix}
                        itemName={question.item_name}
                        choices={question.choices}
                        required={question.required}
                      />
                    )}
                    {question.question_type === 'height_weight' && (
                      <HeightWeight prefix={prefix} required={question.required} />
                    )}
                    {question.question_type === 'menstruation' && (
                      <Menstruation prefix={prefix} required={question.required} />
                    )}
                    {question.question_type === 'datetime' && (
                      <DateTime
                        prefix={prefix}
                        itemName={question.item_name}
                        appointment={appointment}
                        required={question.required}
                        options={question.options}
                      />
                    )}
                    {question.question_type === 'agreement' && question.options && (
                      <Agreement
                        prefix={prefix}
                        itemName={question.item_name}
                        required={question.required}
                        options={question.options}
                      />
                    )}
                    {question.question_type === 'year_month' && (
                      <YearMonth
                        prefix={prefix}
                        itemName={question.item_name}
                        appointment={appointment}
                        patient={patient}
                        required={question.required}
                        options={question.options}
                      />
                    )}
                    {question.question_type === 'history' && (
                      <History
                        prefix={prefix}
                        itemName={question.item_name}
                        required={question.required}
                        options={question.options}
                      />
                    )}
                  </div>
                </div>
              )
            }

            setTimeout(() => {
              const inputName = `${prefix}.value`
              if (getValues(inputName)) {
                setValue(inputName, undefined)
              }
            }, 0)
            return (
              <div key={question.id}>
                <input
                  ref={register({ valueAsNumber: true })}
                  type="hidden"
                  name={`${prefix}.id`}
                />
                <input ref={register} type="hidden" name={`${prefix}.value`} />
              </div>
            )
          })}
          <div className="row justify-content-center pt-5">
            <div className="col-10 col-md-6">
              <button
                type="submit"
                className="btn btn-block btn-primary rounded-pill"
                disabled={disableAction}
              >
                回答する
              </button>
            </div>
          </div>
          {interview.position > 0 && (
            <div className="row justify-content-center pt-5">
              <div className="col-10 col-md-6">
                <button
                  type="button"
                  className="btn btn-block btn-outline-primary rounded-pill"
                  disabled={disableAction}
                  onClick={handlePrev}
                >
                  前の質問に戻る
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </FormProvider>
  )
}

export default InterviewForm
